import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import AdminLangBar from "./AdminLangBar";

export default function AdminUIStrings() {
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);
  const [english, setEnglish] = useState({});
  const [strings, setStrings] = useState(null);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setStrings(null);
    (async () => {
      const [{ data: en }, { data: loc }] = await Promise.all([
        api.get("/admin/pages/ui"),
        api.get("/admin/pages/ui", { params: { lang } }),
      ]);
      setEnglish(en.strings || {});
      setStrings(loc.strings || {});
    })();
  }, [lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/pages/ui", { strings }, { params: { lang } });
      toast.success("Interface text saved");
    } catch (_e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const q = query.toLowerCase();
  const keys = Object.keys(english)
    .sort()
    .filter((k) => !q || k.includes(q) || (english[k] || "").toLowerCase().includes(q) || (strings?.[k] || "").toLowerCase().includes(q));

  return (
    <div className="p-10 lg:p-16 max-w-[1000px]" data-testid="admin-ui-strings">
      <Link to="/admin/translations" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8">
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Translations
      </Link>
      <h1 className="text-4xl lg:text-5xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Interface Text
      </h1>
      <p className="text-sm text-[#73706B] mb-10">Buttons, labels, form fields and section titles used across the public site.</p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey="ui" onTranslated={() => setReloadTick((n) => n + 1)} />

      {!strings ? (
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      ) : (
        <form onSubmit={save} className="space-y-6">
          <input
            data-testid="ui-strings-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search text…"
            className="w-full border border-[#E5DFD5] bg-[#FFFFFF] px-4 py-3 text-sm focus:outline-none focus:border-[#C5A880]"
          />
          {keys.map((k) => (
            <div key={k}>
              <label className="text-[10px] tracking-[0.2em] uppercase text-[#73706B] mb-1 block">{k}</label>
              <input
                data-testid={`ui-string-${k}`}
                value={strings[k] ?? ""}
                onChange={(e) => setStrings({ ...strings, [k]: e.target.value })}
                className="w-full border-b border-[#1A1A1A]/20 bg-transparent py-2 text-sm focus:outline-none focus:border-[#C5A880]"
              />
              {lang !== "en" && <p className="text-[11px] text-[#A8A39A] mt-1">EN: {english[k]}</p>}
            </div>
          ))}
          <div className="sticky bottom-0 bg-[#F9F6F0] py-4 border-t border-[#E5DFD5]">
            <button
              type="submit"
              disabled={saving}
              data-testid="ui-strings-save"
              className="bg-[#1A1A1A] text-[#F9F6F0] px-10 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors disabled:opacity-60"
            >
              {saving ? "Saving" : "Save Interface Text"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
